import { Box } from '@mui/material';
import {
  ProjectsStartingWidget,
  AllocationsExpiringWidget,
  AllocationsNeedingConfirmationWidget,
} from '../../dashboard-widgets';
import type { DashboardWidgets } from '../../dashboard-widgets';
import { pickWidgetGridCols } from '../../dashboard-widgets/utils/dashboardLayout';

interface ProjectsSectionProps {
  widgets: DashboardWidgets | undefined;
  loading: boolean;
  onNavigateToProjects?: () => void;
  onNavigateToPlanning?: () => void;
  /** Planning board with the Proposed filter pre-applied (AC-23). */
  onNavigateToProposedAllocations?: () => void;
}

/**
 * `Projects` — projects-starting + allocations-expiring widgets.
 *
 * Phase 3b: the section registry gates this on
 * `MANAGE_PROJECTS || MANAGE_ALLOCATIONS`, so non-planners never see
 * the chip OR the body (absent-not-masked). No internal permission
 * check is needed here.
 *
 * The at-risk alert lives in `DashboardAlerts` at the top of the
 * dashboard so urgent info is always visible regardless of chip state.
 */
export function ProjectsSection({
  widgets,
  loading,
  onNavigateToProjects,
  onNavigateToPlanning,
  onNavigateToProposedAllocations,
}: ProjectsSectionProps) {
  const cols = pickWidgetGridCols(3);
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: 'repeat(1, 1fr)',
          sm: `repeat(${Math.min(cols, 2)}, 1fr)`,
          md: `repeat(${cols}, 1fr)`,
        },
        gap: 2,
        alignItems: 'stretch',
      }}
    >
      <ProjectsStartingWidget
        items={widgets?.projectsStarting}
        loading={loading}
        onNavigateToProjects={onNavigateToProjects}
      />
      <AllocationsExpiringWidget
        items={widgets?.allocationsExpiring}
        loading={loading}
        onNavigateToPlanning={onNavigateToPlanning}
      />
      {/* Sits beside "Allocations expiring soon" on purpose: both are
          allocation-attention cards a planner acts on from the same place, and
          they share a row vocabulary (avatar · name · urgency chip). */}
      <AllocationsNeedingConfirmationWidget
        items={widgets?.allocationsNeedingConfirmation}
        loading={loading}
        onNavigateToProposed={onNavigateToProposedAllocations}
      />
    </Box>
  );
}
